import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  MapPin, 
  Clock, 
  Eye, 
  AlertCircle,
  Shield,
  Smartphone,
  Globe,
  Filter,
  Calendar,
  Loader2
} from 'lucide-react';

import { WebIDData } from '../../services/webids';
import { getAccessLogs, AccessLog } from '../../services/accessLogs';
import { Button } from '../ui';

/**
 * @component AccessLogs
 * @description Visualización de logs de acceso de los últimos 30 días
 */

interface AccessLogsProps {
  webId: WebIDData;
}

type AccessTypeFilter = 'all' | 'scan' | 'view' | 'emergency' | 'admin';

const AccessLogs: React.FC<AccessLogsProps> = ({ webId }) => {
  const [logs, setLogs] = useState<AccessLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<AccessLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<AccessTypeFilter>('all');
  const [dateFilter, setDateFilter] = useState<'7d' | '30d' | 'all'>('30d');

  useEffect(() => {
    loadAccessLogs();
  }, [webId.id]);

  useEffect(() => {
    filterLogs();
  }, [logs, typeFilter, dateFilter]);

  const loadAccessLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const accessLogs = await getAccessLogs(webId.id, 30); // Últimos 30 días
      setLogs(accessLogs);
    } catch (err) {
      console.error('Error loading access logs:', err);
      setError('Error al cargar historial de acceso');
    } finally {
      setLoading(false);
    }
  };

  const filterLogs = () => {
    let filtered = logs;

    // Filtrar por tipo
    if (typeFilter !== 'all') {
      filtered = filtered.filter(log => log.accessType === typeFilter);
    }

    // Filtrar por fecha
    if (dateFilter !== 'all') {
      const days = dateFilter === '7d' ? 7 : 30;
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      
      filtered = filtered.filter(log => 
        log.timestamp.toDate() >= cutoffDate
      );
    }

    setFilteredLogs(filtered);
  };

  const getAccessTypeIcon = (type: AccessLog['accessType']) => {
    switch (type) {
      case 'scan':
        return <Smartphone className="h-4 w-4 text-blue-600" />;
      case 'view':
        return <Eye className="h-4 w-4 text-green-600" />;
      case 'emergency':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case 'admin':
        return <Shield className="h-4 w-4 text-purple-600" />;
      default:
        return <Activity className="h-4 w-4 text-gray-600" />;
    }
  };

  const getAccessTypeName = (type: AccessLog['accessType']) => {
    switch (type) {
      case 'scan':
        return 'Escaneo NFC';
      case 'view':
        return 'Visualización';
      case 'emergency':
        return 'Acceso Emergencia';
      case 'admin':
        return 'Acceso Admin';
      default:
        return 'Desconocido';
    }
  };

  const getAccessTypeColor = (type: AccessLog['accessType']) => {
    switch (type) {
      case 'scan':
        return 'bg-blue-100 text-blue-800';
      case 'view':
        return 'bg-green-100 text-green-800';
      case 'emergency':
        return 'bg-red-100 text-red-800';
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (timestamp: any) => {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getLocationString = (location?: AccessLog['location']) => {
    if (!location) return 'Ubicación no disponible';
    if (location.address) return location.address;
    return `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`;
  };

  const getBrowserInfo = (userAgent?: string) => {
    if (!userAgent) return 'Navegador desconocido';
    
    // Detectar navegador básico
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    
    return 'Otro navegador';
  };

  const getTypeCount = (type: AccessTypeFilter) => {
    if (type === 'all') return logs.length;
    return logs.filter(log => log.accessType === type).length;
  };

  const typeOptions = [
    { value: 'all', label: 'Todos', count: getTypeCount('all') },
    { value: 'scan', label: 'Escaneos NFC', count: getTypeCount('scan') },
    { value: 'view', label: 'Visualizaciones', count: getTypeCount('view') },
    { value: 'emergency', label: 'Emergencias', count: getTypeCount('emergency') },
    { value: 'admin', label: 'Admin', count: getTypeCount('admin') },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Historial de Acceso
          </h3>
          <p className="text-sm text-gray-600">
            Registro de todos los accesos a tu perfil de emergencia
          </p>
        </div>
        
        <Button
          variant="outline"
          onClick={loadAccessLogs}
          disabled={loading}
        >
          <Activity className="h-4 w-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Accesos</p>
              <p className="text-2xl font-bold text-gray-900">{logs.length}</p>
            </div>
            <Activity className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Escaneos NFC</p>
              <p className="text-2xl font-bold text-blue-600">{getTypeCount('scan')}</p>
            </div>
            <Smartphone className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Emergencias</p>
              <p className="text-2xl font-bold text-red-600">{getTypeCount('emergency')}</p>
            </div>
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Último Acceso</p>
              <p className="text-sm font-medium text-gray-900">
                {logs.length > 0 ? formatDate(logs[0].timestamp) : 'Nunca'}
              </p>
            </div>
            <Clock className="h-8 w-8 text-green-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as AccessTypeFilter)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {typeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} ({option.count})
                </option>
              ))}
            </select>
          </div>

          {/* Date Filter */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value as '7d' | '30d' | 'all')}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="7d">Últimos 7 días</option>
              <option value="30d">Últimos 30 días</option>
              <option value="all">Todos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Logs List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900">
            {filteredLogs.length} acceso{filteredLogs.length !== 1 ? 's' : ''} encontrado{filteredLogs.length !== 1 ? 's' : ''}
          </h4>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredLogs.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No hay accesos registrados
              </h3>
              <p className="text-gray-600">
                {logs.length === 0 
                  ? 'Aún no se han registrado accesos a tu perfil.'
                  : 'No hay accesos que coincidan con los filtros seleccionados.'
                }
              </p>
            </div>
          ) : (
            filteredLogs.map((log) => (
              <div key={log.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      {getAccessTypeIcon(log.accessType)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-medium text-gray-900">
                          {getAccessTypeName(log.accessType)}
                        </h4>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAccessTypeColor(log.accessType)}`}>
                          {log.emergencyContext ? 'Emergencia' : 'Normal'}
                        </span>
                      </div>
                      
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Clock className="h-3 w-3" />
                          <span>{formatDate(log.timestamp)}</span>
                          {log.duration && (
                            <span className="text-gray-500">
                              • Duración: {formatDuration(log.duration)}
                            </span>
                          )}
                        </div>
                        
                        {log.location && (
                          <div className="flex items-center gap-2">
                            <MapPin className="h-3 w-3" />
                            <span>{getLocationString(log.location)}</span>
                          </div>
                        )}
                        
                        {log.userAgent && (
                          <div className="flex items-center gap-2">
                            <Globe className="h-3 w-3" />
                            <span>{getBrowserInfo(log.userAgent)}</span>
                          </div>
                        )}
                        
                        {log.ipAddress && (
                          <div className="flex items-center gap-2">
                            <Shield className="h-3 w-3" />
                            <span>IP: {log.ipAddress}</span>
                          </div>
                        )}
                        
                        {log.accessedBy && (
                          <div className="text-xs text-gray-500">
                            Usuario autenticado: {log.accessedBy}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 mb-1">
              Privacidad y Seguridad
            </h4>
            <p className="text-sm text-blue-700">
              Los logs de acceso se mantienen por 90 días para fines de seguridad. 
              La información de ubicación es aproximada y solo se registra cuando 
              está disponible. Los accesos de emergencia se priorizan y pueden 
              incluir información adicional para asistir a los primeros auxilios.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessLogs;
